/**
 * NDC to CVX Vaccine Lookup Service
 *
 * Loads CDC NDC-CVX mapping data and provides fast lookups by:
 * - GTIN (Sale or Use)
 * - NDC (11-digit or 10-digit, with or without dashes)
 *
 * Data source: CDC IIS Standards
 * https://www2a.cdc.gov/vaccines/iis/iisstandards/downloads/NDC/get_all_ndc_display2.txt
 */

import { normalizeNDC } from '#src/vaccine-barcode-parser.ts'

export interface VaccineInfo {
  cvxCode: string
  cvxDisplay: string
  cvxLongDescription: string
  vaccineName: string
  manufacturer: string
  manufacturerCode: string
  route: string
  ndc11: string
  ndc10: string
  saleGTIN: string
  useGTIN: string
  lotNumber?: string // Will be populated from barcode scan
  expirationDate?: string // Will be populated from barcode scan
}

interface CDCRow {
  saleNDC11: string
  saleNDC10: string
  salePropName: string
  saleLabeler: string
  salePackForm: string
  route: string
  saleStartDate: string
  saleEndDate: string
  saleGTIN: string
  saleLastUpdate: string
  vaccineSeason: string
  useNDC11: string
  useNDC10: string
  useUnitPackForm: string
  useStartDate: string
  useEndDate: string
  useGTIN: string
  useLastUpdate: string
  cvxCode: string
  cvxShortDescription: string
  cvxLongDescription: string
  cvxStatus: string
  cvxEffectiveDate: string
  cvxRetiredDate: string
  mvxCode: string
  mvxManufacturer: string
  mvxStatus: string
  cpt: string
  cptShortDescription: string
  cptLongDescription: string
  cptStatus: string
}

export class VaccineNDCLookup {
  private gtinIndex: Map<string, VaccineInfo[]> = new Map()
  private ndcIndex: Map<string, VaccineInfo[]> = new Map()

  constructor(csvData: string) {
    this.loadData(csvData)
  }

  /**
   * Load and parse CDC mapping file
   */
  private loadData(csvData: string): void {
    const lines = csvData.split('\n')
    if (lines.length < 2) {
      throw new Error('Invalid CDC mapping data: no data rows found')
    }

    // Skip header row (index 0)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i]?.trim()
      if (!line) {
        continue
      }

      try {
        const row = this.parseCDCRow(line)
        if (!row) {
          continue
        }

        // Only index active vaccines
        if (row.cvxStatus !== 'Active') {
          continue
        }

        const vaccineInfo: VaccineInfo = {
          cvxCode: row.cvxCode,
          cvxDisplay: row.cvxShortDescription,
          cvxLongDescription: row.cvxLongDescription,
          vaccineName: row.salePropName,
          manufacturer: row.mvxManufacturer,
          manufacturerCode: row.mvxCode,
          route: row.route,
          ndc11: row.useNDC11,
          ndc10: row.useNDC10,
          saleGTIN: row.saleGTIN,
          useGTIN: row.useGTIN,
        }

        // Index by Sale GTIN
        if (row.saleGTIN) {
          const existing = this.gtinIndex.get(row.saleGTIN) || []
          existing.push(vaccineInfo)
          this.gtinIndex.set(row.saleGTIN, existing)
        }

        // Index by Use GTIN
        if (row.useGTIN) {
          const existing = this.gtinIndex.get(row.useGTIN) || []
          existing.push(vaccineInfo)
          this.gtinIndex.set(row.useGTIN, existing)
        }

        // Index by Use NDC11 (normalized)
        if (row.useNDC11) {
          const normalized = normalizeNDC(row.useNDC11)
          if (normalized) {
            const existing = this.ndcIndex.get(normalized) || []
            existing.push(vaccineInfo)
            this.ndcIndex.set(normalized, existing)
          }
        }

        // Also index by Sale NDC11
        if (row.saleNDC11) {
          const normalized = normalizeNDC(row.saleNDC11)
          if (normalized) {
            const existing = this.ndcIndex.get(normalized) || []
            existing.push(vaccineInfo)
            this.ndcIndex.set(normalized, existing)
          }
        }
      } catch (error) {
        // Skip malformed rows
        console.warn(`Failed to parse CDC row ${i}: ${error}`)
      }
    }
  }

  /**
   * Parse a pipe-delimited CDC row with quoted fields
   */
  private parseCDCRow(line: string): CDCRow | null {
    // Split by pipe, handling quoted fields
    const fields: string[] = []
    let currentField = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]

      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === '|' && !inQuotes) {
        fields.push(currentField)
        currentField = ''
      } else {
        currentField += char
      }
    }
    // Add last field
    fields.push(currentField)

    // Must have exactly 31 fields (all CDC row fields required)
    if (fields.length < 31) {
      return null
    }

    return {
      saleNDC11: fields[0] || '',
      saleNDC10: fields[1] || '',
      salePropName: fields[2] || '',
      saleLabeler: fields[3] || '',
      salePackForm: fields[4] || '',
      route: fields[5] || '',
      saleStartDate: fields[6] || '',
      saleEndDate: fields[7] || '',
      saleGTIN: fields[8] || '',
      saleLastUpdate: fields[9] || '',
      vaccineSeason: fields[10] || '',
      useNDC11: fields[11] || '',
      useNDC10: fields[12] || '',
      useUnitPackForm: fields[13] || '',
      useStartDate: fields[14] || '',
      useEndDate: fields[15] || '',
      useGTIN: fields[16] || '',
      useLastUpdate: fields[17] || '',
      cvxCode: fields[18] || '',
      cvxShortDescription: fields[19] || '',
      cvxLongDescription: fields[20] || '',
      cvxStatus: fields[21] || '',
      cvxEffectiveDate: fields[22] || '',
      cvxRetiredDate: fields[23] || '',
      mvxCode: fields[24] || '',
      mvxManufacturer: fields[25] || '',
      mvxStatus: fields[26] || '',
      cpt: fields[27] || '',
      cptShortDescription: fields[28] || '',
      cptLongDescription: fields[29] || '',
      cptStatus: fields[30] || '',
    }
  }

  /**
   * Lookup vaccine by GTIN (14 digits)
   *
   * @param gtin - 14-digit GTIN from barcode
   * @returns Array of matching vaccines (may be multiple if same GTIN used for multiple CVX codes)
   */
  lookupByGTIN(gtin: string): VaccineInfo[] {
    if (!gtin) {
      return []
    }

    // Normalize GTIN (pad to 14 digits)
    const normalizedGTIN = gtin.padStart(14, '0')

    return this.gtinIndex.get(normalizedGTIN) || []
  }

  /**
   * Lookup vaccine by NDC (with or without dashes)
   *
   * @param ndc - NDC in any format (e.g., "00006-4047-41" or "000064047 41")
   * @returns Array of matching vaccines
   */
  lookupByNDC(ndc: string): VaccineInfo[] {
    if (!ndc) {
      return []
    }

    const normalized = normalizeNDC(ndc)
    if (!normalized) {
      return []
    }

    return this.ndcIndex.get(normalized) || []
  }

  /**
   * Get total number of indexed vaccines
   */
  getIndexSize() {
    return {
      gtins: this.gtinIndex.size,
      ndcs: this.ndcIndex.size,
    }
  }
}

/**
 * Lazily-constructed singleton vaccine lookup service.
 *
 * The CDC NDC-CVX mapping is a ~302 KB raw (~44 KB gzipped) data blob.
 * Importing it eagerly pinned that blob into every `@seen/core` consumer's
 * main bundle via the barrel re-export. Loading `ndc-cvx-mapping.ts` through
 * a dynamic `import()` keeps it in its own chunk, fetched only when a vaccine
 * lookup actually runs (currently just the immunization barcode scanner).
 */
let lookupPromise: Promise<VaccineNDCLookup> | undefined

export function getVaccineNDCLookup(): Promise<VaccineNDCLookup> {
  if (!lookupPromise) {
    lookupPromise = import('#src/data/ndc-cvx-mapping.ts').then(
      ({ ndcCvxMappingData }) => new VaccineNDCLookup(ndcCvxMappingData)
    )
    // Evict a rejected promise so a transient chunk-load failure can retry.
    lookupPromise.catch(() => {
      lookupPromise = undefined
    })
  }
  return lookupPromise
}

/**
 * Lookup vaccine by GTIN
 *
 * @param gtin - 14-digit GTIN from barcode
 * @returns Single vaccine info (first match) or null
 */
export async function lookupVaccineByGTIN(
  gtin: string
): Promise<VaccineInfo | null> {
  const lookup = await getVaccineNDCLookup()
  return lookup.lookupByGTIN(gtin)[0] || null
}

/**
 * Lookup vaccine by NDC
 *
 * @param ndc - NDC in any format
 * @returns Single vaccine info (first match) or null
 */
export async function lookupVaccineByNDC(
  ndc: string
): Promise<VaccineInfo | null> {
  const lookup = await getVaccineNDCLookup()
  return lookup.lookupByNDC(ndc)[0] || null
}
